// What a surface shows when the READ failed — as opposed to when the answer
// was genuinely nothing.
//
// ⚠ WHY THIS IS A COMPONENT AND NOT A ONE-OFF. `useQuery` answers a failure
// with `data: undefined`, and almost every list coalesces that to `[]` before
// rendering. So a failed read and an empty result reach the same branch and
// produce the same sentence — "No bookings yet today", "Nothing waiting for
// dispatch 🎉" — which is a screen stating something it does not know. On an
// operations floor that reads as good news, and the reasonable response to good
// news is to do nothing.
//
// Absence cannot carry two meanings when one of them is operational. This is
// the second meaning, said out loud, and it is deliberately NOT an error dump:
// the reader wants "we don't know yet, don't act on this", not a Postgres
// message.
//
// `apps/partner`'s CashReadFailedCard is the money-carrying variant of the same
// idea and should be rewritten to wrap this once both branches land — its copy
// has to stay specific ("if you are carrying cash it is still yours to hand
// over"), but the shape below is the shared part.

import { ArrowClockwiseIcon, WarningIcon } from "@phosphor-icons/react"

import { Button } from "@bednbody/ui/components/button"
import { cn } from "@bednbody/ui/lib/utils"

function ReadFailedCard({
  message = "This is a connection problem, not an answer — don't read it as “nothing to do”.",
  onRetry,
  isRetrying = false,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & {
  /** Say what could not be read, in the reader's terms. */
  message?: string
  /** Omit where a page-level banner already owns the retry. */
  onRetry?: () => void
  isRetrying?: boolean
}) {
  return (
    <div
      data-slot="read-failed-card"
      role="status"
      className={cn(
        "flex w-full min-w-0 flex-col gap-3 rounded-xl border border-warning/30 bg-warning/5 p-4",
        className
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <WarningIcon weight="fill" className="mt-0.5 size-5 shrink-0 text-warning" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-foreground">We couldn&rsquo;t load this</p>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
      {onRetry ? (
        <Button
          size="sm"
          variant="outline"
          className="self-start"
          disabled={isRetrying}
          onClick={onRetry}
        >
          <ArrowClockwiseIcon weight="bold" className={cn(isRetrying && "animate-spin")} />
          {isRetrying ? "Retrying" : "Try again"}
        </Button>
      ) : null}
    </div>
  )
}

export { ReadFailedCard }
